import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
    <path d="M19.321 5.562a5.124 5.124 0 0 1-3.414-1.267 5.124 5.124 0 0 1-1.537-2.745V1h-3.123v13.267a3.063 3.063 0 0 1-5.505 1.838 3.063 3.063 0 0 1 2.45-4.89v-3.16a6.223 6.223 0 0 0-5.606 6.214 6.223 6.223 0 0 0 6.22 6.217 6.223 6.223 0 0 0 6.22-6.217V9.008a8.21 8.21 0 0 0 4.795 1.534V7.42a5.109 5.109 0 0 1-.5-.016 5.124 5.124 0 0 1 0-1.842z" />
  </svg>
);

export default function Footer() {
  const { t } = useLanguage();

  const LINKS = [
    { href: "/#home", label: t.nav.home },
    { href: "/#services", label: t.nav.services },
    { href: "/#how-it-works", label: t.nav.howItWorks },
    { href: "/blog", label: t.nav.blog },
    { href: "/#quote", label: t.nav.quote },
    { href: "/#faq", label: t.nav.faq },
  ];

  return (
    <footer
      data-testid="site-footer"
      id="contact"
      className="relative bg-[#07070f] border-t border-ink-line pt-16 pb-10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <a href="#home" className="inline-flex items-center gap-3">
              <img src="/logos/truck-mark.jpg" alt="Superstate LLC" className="h-14 md:h-16 rounded" />
              <span className="font-heading text-3xl md:text-4xl text-gold tracking-wide">
                SUPERSTATE <span className="text-white">LLC</span>
              </span>
            </a>
            <p className="mt-4 text-white text-base md:text-lg font-bold max-w-sm leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="mt-6 flex gap-3">
              {[
                { Icon: Instagram, label: "Instagram", href: "#" },
                { Icon: Facebook, label: "Facebook", href: "#" },
                { Icon: Linkedin, label: "LinkedIn", href: "#" },
                { Icon: TikTokIcon, label: "TikTok", href: "#" },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  data-testid={`social-${s.label.toLowerCase()}`}
                  className="w-11 h-11 rounded-full bg-ink-card border border-ink-line flex items-center justify-center text-white hover:text-gold hover:border-gold transition-all"
                >
                  <s.Icon size={20} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading text-2xl md:text-3xl text-white uppercase tracking-wider mb-5">
              {t.footer.quickLinks}
            </h4>
            <ul className="space-y-3">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="text-white hover:text-gold transition-colors text-base md:text-lg font-bold">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-2xl md:text-3xl text-white uppercase tracking-wider mb-5">
              {t.footer.contactTitle}
            </h4>
            <ul className="space-y-4 text-base md:text-lg">
              <li className="flex items-center gap-3 text-white font-bold">
                <Phone size={20} className="text-gold shrink-0" />
                <a href="tel:7865438906" className="hover:text-gold transition-colors">786-543-8906</a>
              </li>
              <li className="flex items-center gap-3 text-white font-bold">
                <Mail size={20} className="text-gold shrink-0" />
                <a
                  href="mailto:dispatch@superstatedispatch.com"
                  className="hover:text-gold transition-colors whitespace-nowrap text-sm md:text-base"
                >
                  dispatch@superstatedispatch.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-white font-bold">
                <MapPin size={20} className="text-gold shrink-0" />
                {t.footer.location}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-ink-line flex flex-col md:flex-row justify-between gap-4 text-sm md:text-base text-white/70 font-semibold">
          <p>{t.footer.copyright}</p>
          <p>
            {t.footer.tagline2Start} <span className="text-gold font-bold">{t.footer.tagline2End}</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
